// Constant-product AMM (x * y = k) used after graduation.
// The pool fee stays in the pool. Reserves can approach but never reach zero.

import { TUNABLES } from "@degen/shared/config/tunables";
import type { SwapResult } from "./bondingCurve";

export interface Pool {
  usd: number;
  tokens: number;
}

const ZERO: SwapResult = { in: 0, out: 0 };
const valid = (x: number) => Number.isFinite(x) && x > 0;

export function poolPrice(p: Pool): number {
  return p.tokens > 0 ? p.usd / p.tokens : 0;
}

export function ammQuoteBuy(p: Pool, usdIn: number, fee: number = TUNABLES.market.ammFee): SwapResult {
  if (!valid(usdIn) || !valid(p.usd) || !valid(p.tokens)) return ZERO;
  const inAfterFee = usdIn * (1 - fee);
  const out = (p.tokens * inAfterFee) / (p.usd + inAfterFee);
  if (!valid(out) || out >= p.tokens) return ZERO;
  return { in: usdIn, out };
}

export function ammBuy(p: Pool, usdIn: number, fee: number = TUNABLES.market.ammFee): SwapResult {
  const r = ammQuoteBuy(p, usdIn, fee);
  if (r.out <= 0) return ZERO;
  p.usd += r.in;
  p.tokens -= r.out;
  return r;
}

export function ammQuoteSell(p: Pool, tokensIn: number, fee: number = TUNABLES.market.ammFee): SwapResult {
  if (!valid(tokensIn) || !valid(p.usd) || !valid(p.tokens)) return ZERO;
  const inAfterFee = tokensIn * (1 - fee);
  const out = (p.usd * inAfterFee) / (p.tokens + inAfterFee);
  if (!valid(out) || out >= p.usd) return ZERO;
  return { in: tokensIn, out };
}

export function ammSell(p: Pool, tokensIn: number, fee: number = TUNABLES.market.ammFee): SwapResult {
  const r = ammQuoteSell(p, tokensIn, fee);
  if (r.out <= 0) return ZERO;
  p.tokens += r.in;
  p.usd -= r.out;
  return r;
}
